import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useTelegramLink } from '@/hooks/useTelegramLink'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { MessageCircle, ExternalLink, Unlink, Check } from 'lucide-react'

interface TelegramLinkDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function TelegramLinkDialog({ open, onOpenChange }: TelegramLinkDialogProps) {
  const { t } = useTranslation()
  const {
    linked,
    loading,
    linkCode,
    botDeepLink,
    generating,
    generateLinkCode,
    unlinkTelegram,
  } = useTelegramLink()

  // Auto-dismiss when linking succeeds
  useEffect(() => {
    if (linked && linkCode) {
      // Small delay so user sees the success state
      const timer = setTimeout(() => {
        onOpenChange(false)
      }, 1500)
      return () => clearTimeout(timer)
    }
  }, [linked, linkCode, onOpenChange])

  const handleGenerateCode = async () => {
    try {
      await generateLinkCode()
    } catch {
      // Error already logged in hook
    }
  }

  const handleUnlink = async () => {
    try {
      await unlinkTelegram()
    } catch {
      // Error already logged in hook
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageCircle className="h-5 w-5" />
            {t('telegram.title')}
          </DialogTitle>
          <DialogDescription>
            {t('telegram.description')}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-8 flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : linked ? (
          // Linked state
          <div className="space-y-4">
            {linkCode ? (
              // Just linked — show success
              <div className="flex items-center justify-center gap-2 py-6 text-gold">
                <Check className="h-6 w-6" />
                <span className="text-lg font-medium">{t('telegram.linkedSuccess')}</span>
              </div>
            ) : (
              // Already linked — show unlink option
              <>
                <Alert variant="success">
                  <MessageCircle className="h-5 w-5" />
                  <AlertDescription>
                    <p className="font-medium">{t('telegram.linked')}</p>
                    <p className="text-xs opacity-80">{t('telegram.linkedDescription')}</p>
                  </AlertDescription>
                </Alert>

                <Button
                  variant="outline"
                  className="w-full text-destructive hover:text-destructive"
                  onClick={handleUnlink}
                >
                  <Unlink className="h-4 w-4 mr-2" />
                  {t('telegram.unlinkTelegram')}
                </Button>
              </>
            )}
          </div>
        ) : botDeepLink ? (
          // Code generated — show deep link + mobile instructions
          <div className="space-y-4">
            <Button
              className="w-full"
              onClick={() => window.open(botDeepLink, '_blank')}
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              {t('telegram.openInTelegram')}
            </Button>

            <div className="p-3 bg-paper-2/40 rounded-lg space-y-2">
              <p className="text-xs font-medium text-muted-foreground">{t('telegram.manualInstructions')}</p>
              <ol className="text-xs text-muted-foreground list-decimal list-inside space-y-1">
                <li>{t('telegram.step1')} <span className="font-mono font-medium text-foreground">{botDeepLink.match(/t\.me\/([^?]+)/)?.[1]}</span></li>
                <li>{t('telegram.step2')} <span className="font-mono font-medium text-foreground">/start {linkCode}</span></li>
              </ol>
            </div>

            <p className="text-xs text-muted-foreground text-center">
              {t('telegram.autoClose')}
            </p>
          </div>
        ) : (
          // Initial state — generate code
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {t('telegram.generateHint')}
            </p>

            <Button
              className="w-full"
              onClick={handleGenerateCode}
              disabled={generating}
            >
              <MessageCircle className="h-4 w-4 mr-2" />
              {generating ? t('telegram.generating') : t('telegram.linkAccount')}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
